import { useState } from 'preact/hooks'
import { AlertTriangle, Check, X } from 'lucide-preact'
import { outboundWebhooksApi } from '@/api/outboundWebhooks'
import { useOutboundWebhookDeliveries } from '@/hooks/useOutboundWebhooks'
import type { OutboundWebhookSubscriptionViewDto } from '@/types/outboundWebhooks'
import { DeliveryRow } from './DeliveryRow'

interface OutboundWebhookDetailProps {
    clientCompanyId: string
    subscription: OutboundWebhookSubscriptionViewDto
    workspaces: { Id: string; Name: string }[]
    onEdit: () => void
    onRotate: () => void
    onDelete: () => void
    onTogglePause: () => void
    /** Refresh the rail so its sparkline and status reflect what just happened here. */
    onChanged: () => void
}

// Mirrors OutboundWebhooks:MaxAttempts. Display only — the server owns the real budget.
const MAX_ATTEMPTS = 5

export function OutboundWebhookDetail({
    clientCompanyId, subscription, workspaces, onEdit, onRotate, onDelete, onTogglePause, onChanged,
}: OutboundWebhookDetailProps) {
    const [statusFilter, setStatusFilter] = useState('')
    const [banner, setBanner] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null)
    const [testing, setTesting] = useState(false)
    const [resendingAll, setResendingAll] = useState(false)

    const { deliveries, counts, loading, error, reload } = useOutboundWebhookDeliveries(clientCompanyId, {
        subscriptionId: subscription.Id,
        status: statusFilter || undefined,
        limit: 50,
    })

    const paused = subscription.Status === 'Paused'
    const workspaceName = (id: string) => workspaces.find(workspace => workspace.Id === id)?.Name ?? id

    const handleTest = async () => {
        setTesting(true)
        setBanner(null)
        const response = await outboundWebhooksApi.sendTest(clientCompanyId, subscription.Id)
        setTesting(false)

        if (response.error || !response.data) {
            setBanner({ tone: 'bad', text: response.error ?? 'The test could not be sent.' })
        } else if (response.data.Delivered) {
            setBanner({ tone: 'ok', text: `Test delivered. ${subscription.Name} returned ${response.data.StatusCode}.` })
        } else {
            // A rejected test is a successful test run with a negative answer — say what came back
            // rather than "something went wrong".
            setBanner({
                tone: 'bad',
                text: `${subscription.Name} rejected the test: ${response.data.Error ?? `HTTP ${response.data.StatusCode}`}`,
            })
        }
        void reload()
        onChanged()
    }

    const handleResendAll = async () => {
        setResendingAll(true)
        setBanner(null)
        const response = await outboundWebhooksApi.resendAllFailed(clientCompanyId, subscription.Id)
        setResendingAll(false)

        if (response.error || !response.data) {
            setBanner({ tone: 'bad', text: response.error ?? 'Nothing could be re-queued.' })
        } else if (response.data.CapReached) {
            // Never let a truncated run read as "everything was handled".
            setBanner({
                tone: 'warn',
                text: `${response.data.Resent} re-queued. More failed deliveries remain — run this again once these finish.`,
            })
        } else {
            setBanner({ tone: 'ok', text: `${response.data.Resent} re-queued.` })
        }
        void reload()
        onChanged()
    }

    const handleResend = async (deliveryId: string) => {
        setBanner(null)
        const response = await outboundWebhooksApi.resendDelivery(clientCompanyId, deliveryId)
        if (response.error) {
            setBanner({ tone: 'bad', text: response.error })
        }
        void reload()
        onChanged()
    }

    return (
        <div className="flex flex-col min-w-0">
            {/* --- header --- */}
            <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-3.5">
                <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className={`text-lg font-semibold tracking-tight ${paused ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                        {subscription.Name}
                    </h3>
                    <StatusPill paused={paused} hasDeliveries={counts.Delivered + counts.Failed + counts.Pending > 0} />
                    <div className="ml-auto flex items-center gap-2">
                        <SecondaryButton onClick={handleTest} disabled={testing}>
                            {testing ? 'Sending' : 'Send test'}
                        </SecondaryButton>
                        <SecondaryButton onClick={onTogglePause}>{paused ? 'Resume' : 'Pause'}</SecondaryButton>
                        <SecondaryButton onClick={onEdit}>Edit</SecondaryButton>
                        <SecondaryButton onClick={onDelete} danger>Delete</SecondaryButton>
                    </div>
                </div>

                <p className="font-mono text-[12.5px] text-slate-500 dark:text-slate-400 break-all">
                    {subscription.Url}
                </p>

                <dl className="flex flex-wrap gap-x-8 gap-y-3">
                    <Meta label="Workspaces">
                        {subscription.WorkspaceId ? `${workspaceName(subscription.WorkspaceId)} only` : 'All workspaces'}
                    </Meta>
                    <Meta label="Signing secret">
                        {/* Not a masked value: there is nothing behind the dots to reveal, because we
                            store the secret encrypted and only ever decrypt it to sign. Dots alone read
                            as "click to show", which is what makes someone dismiss the one-time modal. */}
                        <span className="text-slate-500 dark:text-slate-400">Shown once when issued</span>
                        <button
                            type="button"
                            onClick={onRotate}
                            className="ml-2 px-2 py-0.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
                        >
                            Rotate
                        </button>
                    </Meta>
                    {subscription.Description && <Meta label="Notes">{subscription.Description}</Meta>}
                </dl>
            </div>

            {paused && (
                <div className="mx-6 mt-4 flex gap-2.5 px-3.5 py-3 rounded-lg text-[13px] bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/40">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                    <span className="text-slate-700 dark:text-slate-200">
                        <strong className="font-semibold">Paused.</strong> New leads aren't being sent here.
                        Past deliveries stay readable and can still be resent.
                    </span>
                </div>
            )}

            {banner && <Banner tone={banner.tone} text={banner.text} onDismiss={() => setBanner(null)} />}

            {/* --- tallies --- */}
            <div className="flex gap-7 px-6 py-3.5 border-b border-slate-200 dark:border-slate-700">
                <Tally value={counts.Delivered} label="delivered" className="text-green-600 dark:text-green-400" />
                <Tally value={counts.Failed} label="failed" className="text-red-600 dark:text-red-400" />
                <Tally value={counts.Pending} label="pending" className="text-amber-600 dark:text-amber-400" />
                {counts.Cancelled > 0 && (
                    <Tally value={counts.Cancelled} label="cancelled" className="text-slate-500 dark:text-slate-400" />
                )}
            </div>

            {/* --- log --- */}
            <div className="flex items-center gap-2.5 px-6 pt-3.5 pb-2.5">
                <h4 className="text-[13px] font-semibold text-slate-900 dark:text-white">Deliveries</h4>
                <div className="ml-auto flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter((e.target as HTMLSelectElement).value)}
                        aria-label="Filter deliveries by status"
                        className="px-2.5 py-1 text-[12.5px] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                        <option value="">All statuses</option>
                        <option value="Failed">Failed only</option>
                        <option value="Pending">Pending</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                    <SecondaryButton onClick={handleResendAll} disabled={resendingAll || counts.Failed === 0}>
                        {resendingAll ? 'Re-queuing' : 'Resend all failed'}
                    </SecondaryButton>
                </div>
            </div>

            <div className="flex flex-col px-3 pb-4 min-h-[180px]">
                {loading && <DeliverySkeleton />}

                {!loading && error && (
                    <p role="alert" className="px-3 py-6 text-sm text-red-600 dark:text-red-400">{error}</p>
                )}

                {!loading && !error && deliveries?.length === 0 && (
                    <EmptyDeliveries filtered={Boolean(statusFilter)} onClearFilter={() => setStatusFilter('')} />
                )}

                {!loading && !error && deliveries?.map(delivery => (
                    <DeliveryRow
                        key={delivery.Id}
                        delivery={delivery}
                        maxAttempts={MAX_ATTEMPTS}
                        workspaceName={workspaceName}
                        onResend={handleResend}
                    />
                ))}

                {!loading && !error && (deliveries?.length ?? 0) > 0 && (
                    <p className="px-3 pt-3 text-xs text-slate-500 dark:text-slate-400">
                        Deliveries are queued once a lead finishes processing, after enrichment &mdash; usually
                        within a couple of minutes of the form being submitted, occasionally longer.
                    </p>
                )}
            </div>
        </div>
    )
}

// --- small pieces ----------------------------------------------------------------------------

/**
 * Matches INTEGRATION_STATUS_COLORS: a destination's Active reads exactly like a connection's
 * Active, and Paused/Waiting exactly like its Disabled. Same screen, same vocabulary.
 */
function StatusPill({ paused, hasDeliveries }: { paused: boolean; hasDeliveries: boolean }) {
    const idleClass = 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'

    if (paused) {
        return (
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] font-semibold ${idleClass}`}>
                <span aria-hidden="true">⏸</span>Paused
            </span>
        )
    }
    if (!hasDeliveries) {
        return (
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] font-semibold ${idleClass}`}>
                <span aria-hidden="true">○</span>Waiting
            </span>
        )
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            <span aria-hidden="true">●</span>Active
        </span>
    )
}

function Meta({ label, children }: { label: string; children: preact.ComponentChildren }) {
    return (
        <div className="flex flex-col gap-0.5">
            <dt className="text-[10.5px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500">{label}</dt>
            <dd className="text-[13px] text-slate-700 dark:text-slate-200 flex items-center">{children}</dd>
        </div>
    )
}

function Tally({ value, label, className }: { value: number; label: string; className: string }) {
    return (
        <div className="flex items-baseline gap-1.5">
            <b className={`text-lg font-semibold tracking-tight tabular-nums ${className}`}>{value.toLocaleString()}</b>
            <span className="text-[12.5px] text-slate-500 dark:text-slate-400">{label}</span>
        </div>
    )
}

function SecondaryButton({
    onClick, disabled, danger, children,
}: {
    onClick: () => void
    disabled?: boolean
    danger?: boolean
    children: preact.ComponentChildren
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150 ${danger ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'}`}
        >
            {children}
        </button>
    )
}

function Banner({ tone, text, onDismiss }: { tone: 'ok' | 'warn' | 'bad'; text: string; onDismiss: () => void }) {
    // Washes use the 900/30 dark treatment from the app's status scale rather than a 500-based one:
    // 500 is the brand green, and a brand-tinted "delivered" banner is the collision this whole
    // surface is trying to avoid.
    const toneClass = tone === 'ok'
        ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-800'
        : tone === 'warn'
            ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-800'
            : 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-800'

    const Icon = tone === 'ok' ? Check : AlertTriangle
    const iconClass = tone === 'ok'
        ? 'text-green-600 dark:text-green-300'
        : tone === 'warn' ? 'text-amber-600 dark:text-amber-300' : 'text-red-600 dark:text-red-300'

    return (
        <div role="status" className={`mx-6 mt-4 flex gap-2.5 px-3.5 py-3 rounded-lg text-[13px] border ${toneClass}`}>
            <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconClass}`} aria-hidden="true" />
            <span className="text-slate-700 dark:text-slate-200 flex-1">{text}</span>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
            >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
        </div>
    )
}

function DeliverySkeleton() {
    return (
        <div className="flex flex-col gap-2.5 px-3 py-4" aria-hidden="true">
            {[72, 88, 64, 80, 56].map((width, i) => (
                <div
                    key={i}
                    className="h-3 rounded bg-slate-100 dark:bg-slate-700/60 motion-safe:animate-pulse"
                    style={{ width: `${width}%` }}
                />
            ))}
        </div>
    )
}

function EmptyDeliveries({ filtered, onClearFilter }: { filtered: boolean; onClearFilter: () => void }) {
    if (filtered) {
        return (
            <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Nothing matches that filter</p>
                <button
                    type="button"
                    onClick={onClearFilter}
                    className="text-[13px] text-primary-700 dark:text-primary-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                >
                    Show all deliveries
                </button>
            </div>
        )
    }
    return (
        <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">No deliveries yet</p>
            <p className="text-[12.5px] text-slate-500 dark:text-slate-400 max-w-[42ch] leading-relaxed">
                This destination is set up and waiting. The next lead that finishes processing will
                appear here within about a minute. Leads that already exist aren't sent again.
            </p>
        </div>
    )
}
